import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Select, Card, Statistic, Image, Spin, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, CameraOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getExpensesApi, deleteExpenseApi, changeExpensePaymentStatusApi } from '../../api/expenses';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDate, getPaymentStatusColor } from '../../utils/formatters';
import { PAYMENT_STATUS, EXPENSE_PHOTO } from '../../utils/constants';
import ExpenseDetailModal from '../../components/ExpenseForm/ExpenseDetailModal';

const PAYMENT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'na', label: 'N/A' },
];


export default function ExpensesTab({ cropId, externalTotal, onRefresh }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { farmId } = useParams();
  const [expenses, setExpenses] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailExpense, setDetailExpense] = useState(null);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExpensesApi({ crop_id: cropId });
      setExpenses(res.data?.data || []);
      setTotalExpense(res.data?.totalExpense || 0);
    } catch {
      message.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [cropId]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const refresh = () => { fetchExpenses(); onRefresh?.(); };

  const handleDelete = async (id, amount) => {
    try {
      await deleteExpenseApi({ id, amount, cropId });
      message.success('Expense deleted');
      refresh();
    } catch {
      message.error('Failed to delete expense');
    }
  };

  const handlePaymentToggle = async (expense) => {
    const next = expense.payment_status === 'paid' ? 'unpaid' : 'paid';
    try {
      await changeExpensePaymentStatusApi({ id: expense.id, paymentStatus: next });
      fetchExpenses();
    } catch {}
  };

  const filtered = statusFilter === 'all' ? expenses : expenses.filter((e) => e.payment_status === statusFilter);

  const columns = [
    {
      title: t('photo'), dataIndex: 'photo', width: 60,
      render: (url) => url ? <Image src={EXPENSE_PHOTO(url)} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <CameraOutlined style={{ color: '#ccc' }} />,
    },
    { title: t('expense_type'), dataIndex: 'kharcho', key: 'type', render: (v, record) => (
      <span style={{ cursor: 'pointer', color: '#1a6b2a', fontWeight: 500, textTransform: 'capitalize' }} onClick={() => setDetailExpense(record)}>{v}</span>
    )},
    { title: t('amount'), dataIndex: 'amount', render: (v) => <span style={{ fontWeight: 600, color: '#fa8c16' }}>{formatCurrency(v)}</span> },
    { title: t('date'), dataIndex: 'expense_date', render: formatDate },
    { title: t('note'), dataIndex: 'details' },
    {
      title: t('payment_status'), dataIndex: 'payment_status',
      render: (s, record) => (
        <Tag color={getPaymentStatusColor(s)} style={{ cursor: 'pointer' }} onClick={() => setDetailExpense(record)}>
          {PAYMENT_STATUS[s] || s}
        </Tag>
      ),
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailExpense(record)} />
          <Popconfirm title="Delete this expense?" onConfirm={() => handleDelete(record.id, record.amount)} okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Card style={{ borderRadius: 8, background: '#fff7e6', border: 'none', padding: 0 }} bodyStyle={{ padding: '8px 16px' }}>
          <Statistic title={t('total_expense')} value={formatCurrency(externalTotal || totalExpense)} valueStyle={{ color: '#fa8c16', fontSize: 20 }} />
        </Card>
        <Space>
          <Select value={statusFilter} onChange={setStatusFilter} options={PAYMENT_OPTIONS} style={{ width: 120 }} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/farms/${farmId}/farm/${cropId}/add-expense`)}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('add_expense')}</Button>
        </Space>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : filtered.length === 0 ? (
        <Empty description="No expenses yet">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/farms/${farmId}/farm/${cropId}/add-expense`)}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>{t('add_expense')}</Button>
        </Empty>
      ) : (
        <Table dataSource={filtered} columns={columns} rowKey="id" scroll={{ x: 700 }} />
      )}
      <ExpenseDetailModal
        open={!!detailExpense}
        onClose={() => setDetailExpense(null)}
        expense={detailExpense}
        cropId={cropId}
        onRefresh={fetchExpenses}
      />
    </div>
  );
}
